import Link from 'next/link'
import { AlertTriangle } from 'lucide-react'
import type { ReactNode } from 'react'

interface ErrorStateProps {
  title: string
  body?: string
  icon?: ReactNode
  /** Primary recovery. For an error boundary this is Next's `unstable_retry`. */
  onRetry?: () => void
  retryLabel?: string
  /** Secondary escape hatch, so a member is never stuck on a dead screen. */
  actionLabel?: string
  actionHref?: string
  /** Next's `error.digest`. Shown small so a member can quote it to support. */
  digest?: string
}

/**
 * The something-went-wrong state.
 *
 * Deliberately NOT `EmptyInvite`. That component exists to say "a young
 * community is a feature, not an apology" — gold, dark, celebratory. An error
 * is an apology, and dressing it in the founding-member palette would tell a
 * member their broken page is a designed moment. This is sober: a muted card,
 * plain language, and always at least one way out.
 *
 * Every failure offers two exits where it can — retry for the transient case
 * (a dropped connection, a cold start) and a link elsewhere for the persistent
 * one. A screen with no action is how a member ends up staring at a dead end.
 */
export function ErrorState({
  title,
  body,
  icon,
  onRetry,
  retryLabel = 'Try again',
  actionLabel,
  actionHref,
  digest,
}: ErrorStateProps) {
  return (
    <div className="border border-border-default rounded-2xl bg-white p-9 text-center max-w-[520px] mx-auto">
      <div className="w-12 h-12 rounded-2xl bg-bg-subtle text-text-secondary flex items-center justify-center mx-auto mb-4">
        {icon ?? <AlertTriangle className="w-5 h-5" aria-hidden />}
      </div>

      <div className="text-[18px] font-extrabold tracking-[-0.01em] text-text-primary mb-1.5">
        {title}
      </div>

      {body && (
        <p className="text-[14px] leading-relaxed text-text-muted max-w-[380px] mx-auto mb-5">
          {body}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-center gap-2">
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center justify-center font-bold rounded-lg px-5 py-2.5 text-[14px] bg-brand-primary text-black hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 transition-colors"
          >
            {retryLabel}
          </button>
        )}
        {actionLabel && actionHref && (
          <Link
            href={actionHref}
            className="inline-flex items-center justify-center font-medium rounded-lg px-5 py-2.5 text-[14px] border border-border-default bg-white text-text-primary hover:border-border-hover hover:bg-bg-page focus:outline-none focus:ring-2 focus:ring-border-hover focus:ring-offset-2 transition-colors"
          >
            {actionLabel}
          </Link>
        )}
      </div>

      {digest && (
        <p className="mt-5 text-[11px] text-text-muted font-mono">
          Reference: {digest}
        </p>
      )}
    </div>
  )
}
